/**
 * useFilterUrl Hook
 *
 * Synchronizes filter state with URL query parameters
 */

import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterSet } from '../types';
import { serializeFilters, deserializeFilters } from '../serialization';

// Pure serialization lives in ../serialization (no react-router dependency).
// Re-exported here for backwards-compatible imports.
export { serializeFilters, deserializeFilters };

/**
 * URL parameter name for filters
 */
const FILTERS_PARAM = 'filters';

/**
 * Hook return type
 */
export interface UseFilterUrlReturn {
  /** Parse filters from current URL */
  getFiltersFromUrl: () => FilterSet;
  /** Update URL with new filters */
  setFiltersToUrl: (filters: FilterSet) => void;
  /** Clear filters from URL */
  clearFiltersFromUrl: () => void;
}

/**
 * Hook for syncing filter state with URL
 *
 * @param filterSet - Current filter state
 * @param onFilterSetChange - Callback when URL filters change
 * @param syncDirection - 'both' | 'toUrl' | 'fromUrl'
 * @returns URL sync utilities
 */
export function useFilterUrl(
  filterSet: FilterSet,
  onFilterSetChange?: (filters: FilterSet) => void,
  syncDirection: 'both' | 'toUrl' | 'fromUrl' = 'both'
): UseFilterUrlReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitializedRef = useRef(false);
  const lastSerializedRef = useRef<string>('');

  /**
   * Get filters from URL
   */
  const getFiltersFromUrl = useCallback((): FilterSet => {
    const param = searchParams.get(FILTERS_PARAM);
    return param ? deserializeFilters(param) : [];
  }, [searchParams]);

  /**
   * Set filters to URL
   */
  const setFiltersToUrl = useCallback(
    (filters: FilterSet): void => {
      const serialized = serializeFilters(filters);

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);

          if (serialized) {
            newParams.set(FILTERS_PARAM, serialized);
          } else {
            newParams.delete(FILTERS_PARAM);
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /**
   * Clear filters from URL
   */
  const clearFiltersFromUrl = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(FILTERS_PARAM);
        return newParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Sync URL -> State on mount and URL changes
  useEffect(() => {
    if (syncDirection === 'toUrl') {
      return;
    }

    const urlFilters = getFiltersFromUrl();
    const currentSerialized = serializeFilters(urlFilters);

    // Only update state if URL filters are different
    if (
      onFilterSetChange &&
      (!isInitializedRef.current || currentSerialized !== lastSerializedRef.current)
    ) {
      const currentStateSerialized = serializeFilters(filterSet);

      // Avoid infinite loops
      if (currentSerialized !== currentStateSerialized) {
        lastSerializedRef.current = currentSerialized;
        onFilterSetChange(urlFilters);
      }
    }

    isInitializedRef.current = true;
  }, [searchParams, syncDirection, getFiltersFromUrl, onFilterSetChange, filterSet]);

  // Sync State -> URL when filterSet changes
  useEffect(() => {
    if (syncDirection === 'fromUrl') {
      return;
    }

    // Skip initial render
    if (!isInitializedRef.current) {
      return;
    }

    const currentSerialized = serializeFilters(filterSet);

    // Only update URL if filters are different
    if (currentSerialized !== lastSerializedRef.current) {
      lastSerializedRef.current = currentSerialized;
      setFiltersToUrl(filterSet);
    }
  }, [filterSet, syncDirection, setFiltersToUrl]);

  return {
    getFiltersFromUrl,
    setFiltersToUrl,
    clearFiltersFromUrl,
  };
}
